import { styled } from '@mui/material/styles';
import { Box, Button, Typography } from '@mui/material';

// 계약서 만들기 - Step2(미리보기) 전용 styled (Figma 5183:7852 / 5183:8208)
// 공통 셸(CreateContainer/TopRow/Step*/Card/FooterRow/CancelButton/NextButton)은
// ElectronicContractCreateStyled 에서 재사용한다. 여기엔 미리보기/문서 전용만 둔다.

// ============================================
// 미리보기 카드 본문 (A4 + 줌 컨트롤)
// ============================================
export const PreviewBody = styled(Box)({
  position: 'relative',
  display: 'flex',
  justifyContent: 'center',
  width: '100%',
});

export const ZoomControl = styled(Box)({
  position: 'absolute',
  top: 0,
  right: 0,
  display: 'flex',
  gap: '12px',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '8px 12px',
  border: '1px solid #D9D9D9',
  borderRadius: '8px',
  background: '#fff',
});

export const ZoomText = styled(Typography)({
  fontSize: '24px',
  fontWeight: 500,
  color: '#A6A6A6',
});

// 이전 버튼 (teal 아웃라인)
export const PrevButton = styled(Button)({
  width: '100px',
  height: '48px',
  borderRadius: '4px',
  background: '#fff',
  border: '1px solid #4AB3BC',
  color: '#4AB3BC',
  fontSize: '20px',
  fontWeight: 600,
  boxShadow: 'none',
  textTransform: 'none',
  '&:hover': { background: '#fff', boxShadow: 'none' },
});

// ============================================
// A4 문서
// ============================================
export const A4Page = styled(Box)({
  position: 'relative',
  width: '848px',
  maxWidth: '100%',
  maxHeight: '70vh',
  background: '#fff',
  border: '1px solid #D9D9D9',
  overflowY: 'auto',
  flexShrink: 0,
});

// 페이지 내부 컨텐츠 영역 (좌우 40px 여백 → 콘텐츠 768px)
export const DocBody = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  padding: '40px',
  width: '100%',
});

// 페이지 이동 (◀ n/2 ▶)
export const PageNav = styled(Box)({
  position: 'absolute',
  left: '50%',
  bottom: '48px',
  transform: 'translateX(-50%)',
  display: 'flex',
  gap: '18px',
  alignItems: 'center',
  justifyContent: 'center',
});

export const NavArrow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  color: '#7DC8CF',
  '& svg': { fontSize: 48 },
  '&[data-disabled="true"]': { opacity: 0.35, cursor: 'default' },
});

export const PageIndicator = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100px',
  padding: '12px 24px',
  borderRadius: '999px',
  background: 'rgba(0,0,0,0.64)',
  color: '#fff',
  fontSize: '18px',
  fontWeight: 600,
});

// ============================================
// 문서 헤더 (로고 + 제목)
// ============================================
export const DocHeader = styled(Box)({
  display: 'flex',
  gap: '24px',
  alignItems: 'center',
  width: '100%',
  marginBottom: '16px',
});

export const LogoBox = styled(Box)({
  width: '120px',
  height: '80px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
});

export const DocTitle = styled(Typography)({
  flex: 1,
  fontSize: '32px',
  fontWeight: 700,
  color: '#333',
  textAlign: 'center',
});

// ============================================
// 신규/재등록 + 라커번호
// ============================================
export const TopMetaRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
});

export const CheckGroup = styled(Box)({
  display: 'flex',
  gap: '8px',
  alignItems: 'center',
});

export const CheckItem = styled(Box)({
  display: 'flex',
  gap: '4px',
  alignItems: 'center',
  fontSize: '14px',
  fontWeight: 500,
  color: '#737373',
});

export const CheckSquare = styled(Box)({
  width: '12px',
  height: '12px',
  border: '1px solid #A6A6A6',
  flexShrink: 0,
});

export const SlashText = styled(Typography)({
  fontSize: '14px',
  fontWeight: 500,
  color: '#737373',
});

export const LockerRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '120px',
  fontSize: '14px',
  fontWeight: 500,
  color: '#737373',
});

export const LockerUnderline = styled(Box)({
  flex: 1,
  height: '17px',
  marginLeft: '4px',
  borderBottom: '1px solid #A6A6A6',
});

// ============================================
// 섹션 헤더 (#404040)
// ============================================
export const SectionHeader = styled(Box, {
  shouldForwardProp: prop => prop !== 'rounded',
})(({ rounded = 'top' }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  height: '32px',
  padding: '8px 16px',
  background: '#404040',
  ...(rounded === 'top' && { borderTopLeftRadius: '8px', borderTopRightRadius: '8px' }),
  ...(rounded === 'tl' && { borderTopLeftRadius: '8px' }),
  ...(rounded === 'tr' && { borderTopRightRadius: '8px' }),
  width: '100%',
}));

export const SectionTitle = styled(Typography)({
  fontSize: '18px',
  fontWeight: 600,
  color: '#fff',
});

export const AgreeGroup = styled(Box)({
  display: 'flex',
  gap: '4px',
  alignItems: 'center',
});

export const AgreeText = styled(Typography)({
  fontSize: '12px',
  fontWeight: 400,
  color: '#fff',
});

export const AgreeCheck = styled(Box)({
  width: '16px',
  height: '16px',
  background: '#fff',
  flexShrink: 0,
});

// ============================================
// 표 (라벨/값 셀)
// ============================================
export const TableWrap = styled(Box)({
  display: 'flex',
  width: '100%',
});

export const TableCol = styled(Box, {
  shouldForwardProp: prop => prop !== 'fixed',
})(({ fixed }) => ({
  display: 'flex',
  flexDirection: 'column',
  ...(fixed ? { width: `${fixed}px`, flexShrink: 0 } : { flex: 1, minWidth: 0 }),
}));

// 표 한 쪽(라벨+값) — 가로로 절반 차지 (개인정보 표: 라벨열+값열 가로 배치)
export const Half = styled(Box)({
  display: 'flex',
  flex: 1,
  minWidth: 0,
});

// 시설이용/요금 상세 — 세로 배치 (헤더 + 테이블)
export const HalfCol = styled(Box, {
  shouldForwardProp: prop => prop !== 'borderLeft',
})(({ borderLeft }) => ({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minWidth: 0,
  ...(borderLeft && { borderLeft: '1px solid #404040' }),
}));

// 전체폭 세로 블록 (약관 섹션 래퍼 등)
export const FullCol = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
});

export const LabelCell = styled(Box, {
  shouldForwardProp: prop => prop !== 'cellHeight' && prop !== 'tone' && prop !== 'danger',
})(({ cellHeight = 32, tone, danger }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: `${cellHeight}px`,
  padding: '8px 16px',
  background: tone === 'sum' ? '#D9D9D9' : '#F2F2F2',
  borderBottom: '1px solid #A6A6A6',
  borderRight: '1px solid #A6A6A6',
  borderLeft: '1px solid #A6A6A6',
  fontSize: '16px',
  fontWeight: 500,
  color: danger ? '#EE735A' : '#333',
  whiteSpace: 'nowrap',
}));

export const ValueCell = styled(Box, {
  shouldForwardProp: prop => prop !== 'cellHeight' && prop !== 'align' && prop !== 'danger',
})(({ cellHeight = 32, align = 'flex-start', danger }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: align,
  height: `${cellHeight}px`,
  padding: '8px 16px',
  background: '#fff',
  borderBottom: '1px solid #A6A6A6',
  borderRight: '1px solid #A6A6A6',
  fontSize: '16px',
  fontWeight: 500,
  color: danger ? '#EE735A' : '#a6a6a6',
}));

// 성별 남/여 같은 인라인 체크 그룹 (값 셀 내부)
export const InlineCheckGroup = styled(Box)({
  display: 'flex',
  gap: '8px',
  alignItems: 'center',
});

// 이용기간 값(년/월/일 부터~까지)
export const PeriodBox = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '14px',
  fontWeight: 500,
  color: '#333',
  width: '100%',
});

export const PeriodRow = styled(Box)({
  display: 'flex',
  gap: '24px',
  alignItems: 'center',
  whiteSpace: 'nowrap',
});

// ============================================
// 약관 본문
// ============================================
export const TermBody = styled(Box)({
  width: '100%',
  padding: '8px 16px',
  border: '1px solid #A6A6A6',
  borderBottomLeftRadius: '8px',
  borderBottomRightRadius: '8px',
  fontSize: '12px',
  fontWeight: 600,
  color: '#737373',
  whiteSpace: 'pre-wrap',
  lineHeight: 1.5,
});

// ============================================
// Page2 - 서명/감사 영역
// ============================================
export const SignDateRow = styled(Box)({
  display: 'flex',
  gap: '16px',
  alignItems: 'flex-end',
  justifyContent: 'center',
  width: '100%',
  marginTop: '24px',
  fontSize: '16px',
  fontWeight: 600,
  color: '#333',
});

// 날짜 빈칸 밑줄 (20XX년 __월 __일)
export const SignDateBlank = styled(Box)({
  width: '40px',
  borderBottom: '1px solid #A6A6A6',
  height: '20px',
});

export const SignRow = styled(Box)({
  display: 'flex',
  gap: '64px',
  alignItems: 'flex-start',
  justifyContent: 'center',
  paddingLeft: '120px',
});

export const SignItem = styled(Box)({
  display: 'flex',
  gap: '16px',
  alignItems: 'center',
});

export const SignLabel = styled(Typography)({
  fontSize: '16px',
  fontWeight: 500,
  color: '#a6a6a6',
  whiteSpace: 'nowrap',
});

// 담당자/계약자 옆 이름 밑줄 (서명란)
export const SignNameBlank = styled(Box)({
  width: '100px',
  borderBottom: '1px solid #A6A6A6',
  height: '20px',
});

export const SignStampBox = styled(Box, {
  shouldForwardProp: prop => prop !== 'placeholder',
})({
  width: '80px',
  height: '80px',
  borderRadius: '8px',
  background: 'transparent',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

export const ThanksText = styled(Typography)({
  width: '100%',
  textAlign: 'center',
  color: '#737373',
});

export const ThanksStrong = styled('span')({
  fontSize: '20px',
  fontWeight: 700,
  color: '#737373',
});

export const ThanksNormal = styled('span')({
  fontSize: '18px',
  fontWeight: 500,
  color: '#737373',
});

export const Page2Body = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  alignItems: 'center',
  padding: '0 40px 40px 40px',
  width: '100%',
});
